import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Lock, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ALWAYS_ON, SECTIONS, usePerm } from "../context/PermContext";
import { Loading, PageHeader, Panel } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function ProfileEditor({ p, profiles, onSaved, onDeleted }) {
  const [name, setName] = useState(p.name);
  const [perm, setPerm] = useState(p.permissions);
  const [resp, setResp] = useState(p.responsabili || []);
  const [busy, setBusy] = useState(false);
  const { startPreview } = usePerm();
  const nav = useNavigate();
  useEffect(() => { setName(p.name); setPerm(p.permissions); setResp(p.responsabili || []); }, [p]);
  const dirty = name !== p.name || JSON.stringify(resp) !== JSON.stringify(p.responsabili || []) || JSON.stringify(perm) !== JSON.stringify(p.permissions);
  const save = async () => {
    setBusy(true);
    try { await api(`/web/profiles/${p.id}`, { method: "PUT", body: { name, permissions: perm, responsabili: resp } }); toast.success("Profilo salvato"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Eliminare il profilo "${p.name}"?`)) return;
    try { await api(`/web/profiles/${p.id}`, { method: "DELETE" }); toast.success("Profilo eliminato"); onDeleted(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Panel data-testid="profile-editor">
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex-1 min-w-[200px]">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Nome profilo</p>
          {p.builtin ? <p className="font-display text-2xl font-bold text-[var(--navy)]" data-testid="profile-name">{p.name}</p>
            : <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white" data-testid="profile-name-input" />}
        </div>
        {!p.locked && <Button variant="outline" onClick={() => { startPreview(p.name); nav("/"); }} disabled={dirty} title={dirty ? "Salva prima le modifiche" : ""} className="rounded-full" data-testid="profile-preview-button"><Eye className="w-4 h-4 mr-1" />Anteprima</Button>}
        {!p.locked && <Button onClick={save} disabled={!dirty || busy} className="rounded-full bg-[var(--teal)]" data-testid="profile-save-button"><Save className="w-4 h-4 mr-1" />Salva</Button>}
        {!p.builtin && <Button variant="outline" onClick={del} className="rounded-full text-red-600" data-testid="profile-delete-button"><Trash2 className="w-4 h-4 mr-1" />Elimina</Button>}
      </div>
      {p.locked && <p className="flex items-center gap-2 text-sm text-slate-600 mb-6 rounded-xl bg-slate-50 p-3" data-testid="profile-locked"><Lock className="w-4 h-4" />Il Direttore ha sempre tutti i permessi.</p>}
      <div className="space-y-6">
        {SECTIONS.map((s) => (
          <div key={s.key}>
            <p className="font-semibold text-[var(--navy)] mb-2">{s.label}</p>
            <div className="rounded-xl border divide-y">{s.acts.map(([k, l, hint]) => (
              <label key={k} className="flex items-center justify-between gap-4 px-4 py-3 cursor-pointer">
                <span><span className="text-sm font-medium text-[var(--navy)]">{l}</span>{hint && <span className="block text-xs text-slate-500">{hint}</span>}</span>
                <Switch checked={!!perm[k]} disabled={p.locked} onCheckedChange={(v) => setPerm({ ...perm, [k]: v })} data-testid={`perm-${k.replace(".", "-")}`} />
              </label>))}
              {s.key === "report" && !p.locked && (
                <div className="px-4 py-3" data-testid="profile-responsabili">
                  <p className="text-sm font-medium text-[var(--navy)]">Responsabili del settore</p>
                  <p className="text-xs text-slate-500 mb-3">Ricevono con il Direttore le notifiche dei report inviati e mancanti. {resp.length === 0 && "Ora: solo il Direttore."}</p>
                  <div className="grid sm:grid-cols-2 gap-2">{profiles.filter((x) => x.name !== p.name && !x.locked).map((x) => (
                    <label key={x.id} className="flex items-center gap-2 text-sm text-[var(--navy)] cursor-pointer">
                      <Checkbox checked={resp.includes(x.name)} onCheckedChange={(v) => setResp(v ? [...resp, x.name] : resp.filter((r) => r !== x.name))} data-testid={`profile-resp-${slug(x.name)}`} />{x.name}
                    </label>))}
                  </div>
                </div>)}
            </div>
          </div>))}
        <p className="text-xs text-slate-500" data-testid="profile-always-on">Sempre attive per tutti: {ALWAYS_ON.join(", ")}.</p>
      </div>
    </Panel>
  );
}

export default function AdminProfiles() {
  const { profiles, reload } = usePerm();
  const [sel, setSel] = useState(null);
  const [newName, setNewName] = useState("");
  useEffect(() => { reload(); }, [reload]);
  if (!profiles) return <Loading />;
  const current = profiles.find((p) => p.id === sel) || profiles[0];
  const create = async () => {
    if (newName.trim().length < 2) return toast.error("Scrivi il nome del nuovo profilo");
    try { const p = await api("/web/profiles", { method: "POST", body: { name: newName.trim(), permissions: {} } }); setNewName(""); await reload(); setSel(p.id); toast.success("Profilo creato: ora attiva le sue attività"); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div>
      <PageHeader subtitle="Pannello Direttore" title="Gestione Profili" />
      <p className="text-sm text-slate-600 mb-6 max-w-3xl">Per ogni profilo scegli le attività visibili nella web app. Chi ha più profili vede la somma dei permessi. I profili si assegnano dalla scheda del dipendente. Il server dell'app mobile continua ad applicare le sue regole.</p>
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <div className="space-y-3">
          <Panel className="!p-4">
            <p className="text-sm font-semibold text-[var(--navy)] mb-2">Profilo da modificare</p>
            <Select value={current?.id} onValueChange={setSel}>
              <SelectTrigger className="bg-white" data-testid="profiles-select"><SelectValue /></SelectTrigger>
              <SelectContent>{profiles.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`profile-item-${slug(p.name)}`}>{p.name}{!p.builtin ? " (nuovo)" : ""}</SelectItem>)}</SelectContent>
            </Select>
          </Panel>
          <Panel className="!p-4">
            <p className="text-sm font-semibold text-[var(--navy)] mb-2">Nuovo profilo</p>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Es. Stagionale banchina" className="bg-white mb-2" data-testid="new-profile-input" />
            <Button onClick={create} className="w-full rounded-full bg-[var(--navy)]" data-testid="new-profile-button"><Plus className="w-4 h-4 mr-1" />Crea profilo</Button>
          </Panel>
        </div>
        {current && <ProfileEditor p={current} profiles={profiles} onSaved={reload} onDeleted={() => { setSel(null); reload(); }} />}
      </div>
    </div>
  );
}
